// NOT part of the upstream port - a small, deliberate delta, narrow and
// documented rather than a silent behavior change (same discipline as this
// port's other verbatim-but-justified deltas, e.g. `calculatePlaybackTimes`'s
// optional chaining or `legacyPlaylistId`'s hash swap).
//
// ## The gap this closes
//
// `SignageSurface.tsx` (Foyer's current renderer) corrects its wall-clock
// against the server's own `generatedAt` on every poll before resolving
// anything - a TV's RTC has "no NTP guarantee" (that file's own words), so a
// panel with a drifting clock would otherwise resolve the wrong interval, or
// nothing at all, while reporting the perfectly innocent "nothing scheduled
// right now".
//
// The ported machine's own wall-clock-key lookups (`evaluateSchedule` in
// actions.ts, `getClosestKeyBeforeCurrentTime` in scheduleUtils.ts) call
// `new Date()` directly - the real fielded autoplayer's own behavior,
// uncorrected, because on a real device its clock IS what's being compared
// against (there is no separate "true" clock available to it either). Foyer
// DOES have one: the timeline poll's own HTTP response carries the server's
// `generatedAt`. This module is the seam that lets `SignageEngineSurface.tsx`
// (Foyer glue, not signage-engine/) feed that correction in, without editing
// the ported files any more than the two call sites strictly need.
//
// Default is a no-op (0ms skew) - a caller that never calls `setClockSkewMs`
// (every existing test, every use before this file existed) gets EXACTLY the
// original `new Date()` behavior. This is deliberately a plain module-level
// value, not React state or a machine-context field: both call sites are
// plain functions with no access to either, and re-threading a value through
// the whole ported call chain (context -> guards -> actions -> here) for a
// single number would be a far larger, riskier diff than one shared getter.
let skewMs = 0;

/**
 * How far the device's own clock is from the server's, in milliseconds.
 * Positive means the device is BEHIND (matches `SignageSurface.tsx`'s own
 * `skewMs` sign convention exactly, for anyone diffing the two).
 */
export function setClockSkewMs(ms: number): void {
  skewMs = Number.isFinite(ms) ? ms : 0;
}

/** For tests: read back whatever the last caller set. */
export function getClockSkewMs(): number {
  return skewMs;
}

/** The corrected "now" the two wall-clock-key lookups use instead of a bare
 *  `new Date()`. */
export function skewedNow(): Date {
  return new Date(Date.now() + skewMs);
}
